import { sedrApi, type SedrStarterSetFolder } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { sedrErrorMessageKey } from './sedrErrors';

export function showSedrLibraryFolderDialog(options: {
  setId: string;
  folders: SedrStarterSetFolder[];
  folderId?: string;
  parentId?: string | null;
  initialName?: string;
  onDone: () => void | Promise<void>;
}): void {
  const isRename = Boolean(options.folderId);
  const shell = createModalShell({
    titleKey: isRename ? 'sedr.library.renameFolderTitle' : 'sedr.library.addFolderTitle',
    titleId: 'sedr-library-folder-dialog-title',
    helpTopic: dialogHelpTopic('sedrLibrary'),
  });

  const parentOptions = options.folders
    .filter((f) => f.id !== options.folderId)
    .map((f) => `<option value="${f.id}"></option>`)
    .join('');

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-library-folder-form">
      <label>
        <span data-i18n="sedr.library.colFolderName"></span>
        <input type="text" name="name" maxlength="200" required autocomplete="off" />
      </label>
      <label>
        <span data-i18n="sedr.library.parentFolderLabel"></span>
        <select name="parentId">
          <option value="" data-i18n="sedr.library.parentFolderRoot"></option>
          ${parentOptions}
        </select>
      </label>
      <p class="error-text" id="sedr-library-folder-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.setAttribute('data-i18n', 'common.cancel');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'sedr-library-folder-form');
  submitButton.setAttribute('data-i18n', isRename ? 'files.owner.rename' : 'sedr.library.addFolder');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#sedr-library-folder-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#sedr-library-folder-error') as HTMLParagraphElement;
  const nameInput = form.querySelector('input[name="name"]') as HTMLInputElement;
  const parentSelect = form.querySelector('select[name="parentId"]') as HTMLSelectElement;

  for (const option of parentSelect.querySelectorAll('option[value]:not([value=""])')) {
    const folder = options.folders.find((f) => f.id === (option as HTMLOptionElement).value);
    if (folder) {
      option.textContent = folder.name;
    }
  }

  nameInput.value = options.initialName ?? '';
  parentSelect.value = options.parentId ?? '';
  let submitting = false;
  let lastErrorKey = 'status.error';

  const refreshLocale = () => {
    if (!errorEl.hidden) {
      errorEl.textContent = t(lastErrorKey);
    }
    applyI18n(shell.dialog);
  };
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    if (submitting) return;
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);
  nameInput.focus();
  nameInput.select();

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submitting) return;

    void (async () => {
      submitting = true;
      errorEl.hidden = true;
      submitButton.disabled = true;
      const data = new FormData(form);
      const name = String(data.get('name') ?? '').trim();
      const parentId = String(data.get('parentId') ?? '').trim() || null;
      try {
        if (isRename && options.folderId) {
          await sedrApi.patchStarterSetFolder(options.setId, options.folderId, { name, parentId });
        } else {
          await sedrApi.createStarterSetFolder(options.setId, { name, parentId });
        }
        submitting = false;
        unbindLocale();
        unbindDismiss();
        shell.backdrop.remove();
        await options.onDone();
      } catch (cause) {
        submitting = false;
        submitButton.disabled = false;
        lastErrorKey = sedrErrorMessageKey(cause);
        errorEl.hidden = false;
        errorEl.textContent = t(lastErrorKey);
      }
    })();
  });
}
